import type {Dungeon} from "@/dungeons/model/Dungeon.ts";
import {gridToWorld} from "@/dungeons/geometry/Coordinates.ts";
import AnnotationPopover from "@/dungeons/annotations/AnnotationPopover.tsx";
import {useEncounterViewPort} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import DungeonDoorPopover from "./DungeonDoorPopover.tsx";
import type {GameMasterAnnotationsController} from "./useGameMasterAnnotations.ts";
import type {MapFeatureSelection} from "./useMapFeatureSelection.ts";
import {useEncounterLights} from "@/encounter/lights/EncounterLightHooks.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {effectiveLights} from "@/tabletop/visibility/LightGeometry.ts";
import EncounterLightPopover from "./EncounterLightPopover.tsx";
import {CharacterSizes} from "@/characters/model/CharacterSizes.ts";
import {lightMarkerLayout} from "./LightMarkerLayout.ts";
import {isLightLocked, lightLockKey} from "./LightLockState.ts";

type DungeonLevel = Dungeon["levels"][number];

export default function GameMasterMapPopovers({activeLevel, selection, toggledLightLocks,
    onToggleLightLock, annotations, showAnnotations, viewport, onCloseAnnotation}: {
    activeLevel?: DungeonLevel;
    selection?: MapFeatureSelection;
    toggledLightLocks: ReadonlySet<string>;
    onToggleLightLock: (key: string) => void;
    annotations: GameMasterAnnotationsController;
    showAnnotations: boolean;
    viewport: {width: number; height: number};
    onCloseAnnotation: () => void;
}) {
    const {mapTransform} = useEncounterViewPort();
    const {state: lights, persistedState: savedLights} = useEncounterLights();
    const {combatants} = useCombatants();
    const selectedDoor = selection?.type === "door" && selection.levelId === activeLevel?.id
        ? activeLevel.geometry.doors.find(door => door.id === selection.id) : undefined;
    const annotationAnchor = annotations.selected && activeLevel
        ? gridToWorld(annotations.selected.annotation.position, activeLevel.geometry.grid) : undefined;
    const doorAnchor = selectedDoor && activeLevel ? gridToWorld({
        x: (selectedDoor.start.x + selectedDoor.end.x) / 2,
        y: (selectedDoor.start.y + selectedDoor.end.y) / 2,
    }, activeLevel.geometry.grid) : undefined;
    const selectedLight = selection?.type === "light" && activeLevel && selection.levelId === activeLevel.id
        ? effectiveLights(activeLevel.geometry, activeLevel.id, lights, combatants)
            .find(light => light.id === selection.id) : undefined;
    const lightSource = combatants.find(item => item.id === selectedLight?.combatantId);
    const lightAnchor = selectedLight ? lightMarkerLayout(selectedLight.origin,
        lightSource ? CharacterSizes.get(lightSource.character.size) : undefined).position : undefined;
    const screenPoint = (world: {x: number; y: number}) => ({
        x: mapTransform.x + world.x * mapTransform.scale,
        y: mapTransform.y + world.y * mapTransform.scale,
    });

    return <>
        {showAnnotations && annotations.selected && annotationAnchor && activeLevel &&
            <AnnotationPopover
                key={`${annotations.selected.source}:${annotations.selected.annotation.id}:${annotations.editing}`}
                annotation={annotations.selected.annotation} editable={annotations.selected.source === "encounter"}
                isNew={annotations.draft?.id === annotations.selected.annotation.id}
                editing={annotations.editing} busy={annotations.busy} error={annotations.mutationError}
                anchor={screenPoint(annotationAnchor)} viewport={viewport}
                onEdit={() => annotations.setEditing(true)} onSave={annotation => void annotations.save(annotation)}
                onDelete={() => void annotations.deleteSelected()} onClose={onCloseAnnotation}/>}
        {selectedDoor && doorAnchor && <DungeonDoorPopover key={selectedDoor.id}
            door={selectedDoor} anchor={screenPoint(doorAnchor)} viewport={viewport}/>}
        {selectedLight && lightAnchor && activeLevel && <EncounterLightPopover key={selectedLight.id} light={selectedLight}
            encounterLight={lights.lights.find(light => light.id === selectedLight.id)}
            dungeonLight={activeLevel?.geometry.lights.find(light => light.id === selectedLight.id)}
            hasOverride={savedLights.overrides.some(item => item.lightId === selectedLight.id)}
            locked={isLightLocked(selectedLight.source,
                lightLockKey(activeLevel.id, selectedLight.id), toggledLightLocks)}
            onToggleLock={() => onToggleLightLock(lightLockKey(activeLevel.id, selectedLight.id))}
            anchor={screenPoint(lightAnchor)} viewport={viewport} onClose={onCloseAnnotation}/>}
        {annotations.loadError && <div role="alert"
            className="absolute bottom-2 left-2 rounded bg-destructive p-2 text-sm text-white">
            Encounter-Annotationen konnten nicht geladen werden.
        </div>}
    </>;
}
